import { useEffect } from "react"
import { $getListDepth, $isListItemNode, $isListNode } from "@lexical/list"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { mergeRegister } from "@lexical/utils"
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from "lexical"

export default function ListMaxIndentLevelPlugin({ maxDepth = 7 }: { maxDepth?: number }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return mergeRegister(
      // Shift+Tab should always outdent regardless of cursor position.
      // TabIndentationPlugin only outdents when cursor is at block start;
      // this handler fires first (COMMAND_PRIORITY_LOW > COMMAND_PRIORITY_EDITOR)
      // to ensure Shift+Tab always dispatches OUTDENT_CONTENT_COMMAND.
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event) => {
          if (!event.shiftKey) return false

          const selection = $getSelection()
          if (!$isRangeSelection(selection)) return false

          event.preventDefault()
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
          return true
        },
        COMMAND_PRIORITY_LOW
      ),

      // Limit list indent depth: block if no previous sibling (prevents Tab-spamming
      // on the same line) or if already at max depth.
      editor.registerCommand(
        INDENT_CONTENT_COMMAND,
        () => {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) return false

          const nodes = selection.getNodes()
          for (const node of nodes) {
            const listItem = $isListItemNode(node) ? node : node.getParent()
            if (!$isListItemNode(listItem)) continue

            if (listItem.getPreviousSibling() === null) {
              return true
            }

            const parentList = listItem.getParent()
            if ($isListNode(parentList) && $getListDepth(parentList) > maxDepth) {
              return true
            }
          }

          return false
        },
        COMMAND_PRIORITY_HIGH
      )
    )
  }, [editor, maxDepth])

  return null
}
